// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { DetailedAsset } from '@/asset/api/types';
import { DepreciationChartTooltip } from '@/asset/components/depreciation-chart-tooltip';
import {
  CHART_HEIGHT_PX,
  HoverDatum,
  NormalizedPeriod,
  SVG_MARGIN,
  TOOLTIP_VERTICAL_OFFSET_PX,
  getAxisMax,
  getLabelFrequency,
  getMarkers,
  getMonthTicks,
  getPostServicePoints,
  haveSameInstant,
  haveSamePeriods,
  layoutMarkers,
  normalizeDepreciationPeriods,
} from '@/asset/components/depreciation-chart-utilities';
import { IsoCurrencyCode } from '@/currency/constants/iso';
import { useResizeObserver } from '@/hooks/use-resize-observer';
import { formatCurrency } from '@/utilities/number';
import { Box, useTheme } from '@mui/material';
import { AxisLeft, AxisRight } from '@visx/axis';
import { scaleLinear, scaleTime } from '@visx/scale';
import { defaultStyles, useTooltip, useTooltipInPortal } from '@visx/tooltip';
import { DateTime } from 'luxon';
import { MouseEvent, memo, useId, useRef } from 'react';

const MARKER_LABEL_OFFSET_PX = -20;
const MARKER_DATE_OFFSET_PX = -6;
const MARKER_ROW_HEIGHT_PX = 32;

interface DepreciationChartProps {
  acquisitionCost: number | null;
  capitalizedCost: number | null;
  chartEndDate: DateTime;
  currentDate: DateTime;
  disposalDate: DateTime | null;
  endOfUsefulLife: DateTime | null;
  inServiceDate: DateTime | null;
  inventoryDate: DateTime;
  currencyCode: IsoCurrencyCode;
  periods: NonNullable<DetailedAsset['accounting']['depreciationPeriods']>;
  salvageValue: number | null;
  svgRef: { current: SVGSVGElement | null };
  usefulLifeMonths: number | null;
}

function DepreciationChartComponent(props: DepreciationChartProps) {
  const theme = useTheme();
  const futureBarPatternId = useId().replace(/:/g, '-');
  const containerRef = useRef<HTMLDivElement>(null);
  const containerSize = useResizeObserver(containerRef);
  const {
    tooltipData,
    tooltipLeft,
    tooltipOpen,
    tooltipTop,
    showTooltip,
    hideTooltip,
  } = useTooltip<HoverDatum>();
  const { containerRef: tooltipContainerRef, TooltipInPortal } =
    useTooltipInPortal({
      detectBounds: true,
      scroll: true,
    });

  const periods = normalizeDepreciationPeriods(props.periods);
  const domainStart = props.inventoryDate.startOf('month').minus({ months: 1 });
  const domainVisibleEnd = props.chartEndDate
    .endOf('month')
    .plus({ months: 1 })
    .startOf('month');
  const domainEnd = domainVisibleEnd.plus({ months: 1 });
  const innerHeight = CHART_HEIGHT_PX - SVG_MARGIN.top - SVG_MARGIN.bottom;
  const containerWidth = Math.max(
    containerSize.width - SVG_MARGIN.left - SVG_MARGIN.right,
    320
  );
  const innerWidth = containerWidth;
  const svgWidth = innerWidth + SVG_MARGIN.left + SVG_MARGIN.right;
  const svgHeight = CHART_HEIGHT_PX;

  const xScale = scaleTime({
    domain: [domainStart.toJSDate(), domainEnd.toJSDate()],
    range: [0, innerWidth],
  });
  const leftAxisMax = getAxisMax([
    props.acquisitionCost,
    props.capitalizedCost,
    props.salvageValue,
    ...periods.map((period) => period.bookValue),
  ]);
  const rightAxisMax = getAxisMax(periods.map((period) => period.depreciation));
  const leftScale = scaleLinear({
    domain: [0, leftAxisMax],
    nice: true,
    range: [innerHeight, 0],
  });
  const rightScale = scaleLinear({
    domain: [0, rightAxisMax],
    nice: true,
    range: [innerHeight, 0],
  });

  const markerData = layoutMarkers({
    markers: getMarkers({
      chartEndDate: props.chartEndDate,
      currentDate: props.currentDate,
      disposalDate: props.disposalDate,
      endOfUsefulLife: props.endOfUsefulLife,
      inServiceDate: props.inServiceDate,
      inventoryDate: props.inventoryDate,
      theme,
    }),
    xScale: (date) => xScale(date),
  });
  const currentMonthStart = props.currentDate.startOf('month');
  const preServiceEnd =
    props.inServiceDate !== null &&
    props.inServiceDate.valueOf() < props.chartEndDate.valueOf()
      ? props.inServiceDate
      : props.chartEndDate;
  const preServiceValue = props.acquisitionCost ?? props.capitalizedCost ?? 0;
  const postServiceValue = props.capitalizedCost ?? preServiceValue;
  const postServicePoints = getPostServicePoints({
    capitalizedCost: postServiceValue,
    chartEndDate: props.chartEndDate,
    inServiceDate: props.inServiceDate,
    periods,
  });
  const xAxisMonths = getMonthTicks({
    endDate: domainVisibleEnd,
    frequency: getLabelFrequency(
      domainVisibleEnd.diff(domainStart, 'months').months + 1
    ),
    startDate: domainStart,
  });

  return (
    <Box
      ref={containerRef}
      sx={{ maxWidth: '100%', minWidth: 0, overflow: 'hidden', width: '100%' }}
    >
      <Box
        sx={{
          maxWidth: '100%',
          minWidth: 0,
          overflow: 'hidden',
          width: '100%',
        }}
      >
        <svg
          data-testid="depreciation-chart-svg"
          ref={(node) => {
            props.svgRef.current = node;
          }}
          height={svgHeight}
          preserveAspectRatio="xMinYMin meet"
          viewBox={`0 0 ${svgWidth} ${svgHeight}`}
          width="100%"
          style={{ display: 'block', height: 'auto', maxWidth: '100%' }}
        >
          <defs>
            <pattern
              id={futureBarPatternId}
              width="10"
              height="10"
              patternUnits="userSpaceOnUse"
              patternTransform="rotate(45)"
            >
              <rect
                width="10"
                height="10"
                fill={theme.palette.primary.light}
                opacity="0.35"
              />
              <line
                x1="0"
                y1="0"
                x2="0"
                y2="10"
                stroke={theme.palette.primary.main}
                strokeWidth="4"
              />
            </pattern>
          </defs>
          <text
            fill={theme.palette.text.secondary}
            fontSize={12}
            textAnchor="middle"
            transform={`translate(18 ${SVG_MARGIN.top + innerHeight / 2}) rotate(-90)`}
          >
            Net book value
          </text>
          <text
            fill={theme.palette.text.secondary}
            fontSize={12}
            textAnchor="middle"
            transform={`translate(${svgWidth - 18} ${SVG_MARGIN.top + innerHeight / 2}) rotate(90)`}
          >
            Depreciation in month
          </text>
          <g
            ref={tooltipContainerRef}
            transform={`translate(${SVG_MARGIN.left},${SVG_MARGIN.top})`}
          >
            <line
              x1={0}
              x2={0}
              y1={0}
              y2={innerHeight}
              stroke={theme.palette.divider}
              strokeWidth={1}
            />
            <line
              x1={0}
              x2={innerWidth}
              y1={innerHeight}
              y2={innerHeight}
              stroke={theme.palette.divider}
              strokeWidth={1}
            />
            <AxisLeft
              scale={leftScale}
              stroke={theme.palette.divider}
              tickStroke={theme.palette.divider}
              tickFormat={(value) =>
                formatCurrency(Number(value), {
                  abbreviate: true,
                  digits: 1,
                  hideZeroDecimals: true,
                  currencyCode: props.currencyCode,
                })
              }
              tickLabelProps={() => ({
                fill: theme.palette.text.secondary,
                fontSize: 12,
                textAnchor: 'end',
              })}
            />
            <AxisRight
              left={innerWidth}
              scale={rightScale}
              stroke={theme.palette.divider}
              tickStroke={theme.palette.divider}
              tickFormat={(value) =>
                formatCurrency(Number(value), {
                  abbreviate: true,
                  digits: 1,
                  hideZeroDecimals: true,
                  currencyCode: props.currencyCode,
                })
              }
              tickLabelProps={() => ({
                fill: theme.palette.text.secondary,
                fontSize: 12,
                textAnchor: 'start',
              })}
            />

            {props.salvageValue !== null && props.salvageValue > 0 && (
              <line
                x1={0}
                x2={innerWidth}
                y1={leftScale(props.salvageValue)}
                y2={leftScale(props.salvageValue)}
                stroke={theme.palette.grey[500]}
                strokeDasharray="6 4"
                strokeWidth={1.5}
              />
            )}

            {markerData.map((marker) => {
              const rowShiftPx = marker.row * MARKER_ROW_HEIGHT_PX;
              return (
                <g key={`${marker.variant}-${marker.label}`}>
                  <line
                    x1={marker.x}
                    x2={marker.x}
                    y1={0}
                    y2={innerHeight}
                    stroke={marker.color}
                    strokeDasharray={
                      marker.variant === 'current' ? undefined : '5 4'
                    }
                    strokeWidth={marker.variant === 'current' ? 2 : 1.5}
                  />
                  <text
                    fill={marker.color}
                    fontSize={11}
                    textAnchor="middle"
                    x={marker.x}
                    y={MARKER_LABEL_OFFSET_PX - rowShiftPx}
                  >
                    {marker.label}
                  </text>
                  <text
                    fill={theme.palette.text.secondary}
                    fontSize={10}
                    textAnchor="middle"
                    x={marker.x}
                    y={MARKER_DATE_OFFSET_PX - rowShiftPx}
                  >
                    {marker.date.toFormat('MMM yyyy')}
                  </text>
                </g>
              );
            })}

            {periods.map((period, index) => {
              const periodStart = period.date;
              const nextPeriodStart =
                periods[index + 1]?.date ??
                props.chartEndDate.plus({ days: 1 }).startOf('day');
              const barX = xScale(periodStart.toJSDate()) + 2;
              const barWidth = Math.max(
                xScale(nextPeriodStart.toJSDate()) -
                  xScale(periodStart.toJSDate()) -
                  4,
                2
              );
              const barHeight = innerHeight - rightScale(period.depreciation);
              const isFuturePeriod =
                period.date.startOf('month').valueOf() >
                currentMonthStart.valueOf();

              return (
                <g key={period.date.toISODate()}>
                  {period.depreciation > 0 && (
                    <rect
                      fill={
                        isFuturePeriod
                          ? `url(#${futureBarPatternId})`
                          : theme.palette.primary.main
                      }
                      height={barHeight}
                      opacity={0.85}
                      rx={2}
                      stroke={theme.palette.primary.main}
                      strokeWidth={isFuturePeriod ? 1 : 0}
                      width={barWidth}
                      x={barX}
                      y={rightScale(period.depreciation)}
                    />
                  )}
                  <rect
                    fill="transparent"
                    height={innerHeight}
                    width={barWidth}
                    x={barX}
                    y={0}
                    onMouseLeave={hideTooltip}
                    onMouseMove={(event) =>
                      handleTooltipMove({
                        barWidth,
                        barX,
                        event,
                        period,
                        periodEnd: nextPeriodStart.minus({ days: 1 }),
                        showTooltip,
                      })
                    }
                  />
                </g>
              );
            })}

            <line
              stroke={theme.palette.secondary.main}
              strokeWidth={2}
              x1={xScale(props.inventoryDate.toJSDate())}
              x2={xScale(preServiceEnd.toJSDate())}
              y1={leftScale(preServiceValue)}
              y2={leftScale(preServiceValue)}
            />

            {props.inServiceDate !== null &&
              props.chartEndDate.valueOf() >= props.inServiceDate.valueOf() &&
              props.acquisitionCost !== null &&
              props.capitalizedCost !== null &&
              props.acquisitionCost !== props.capitalizedCost && (
                <line
                  stroke={theme.palette.secondary.main}
                  strokeWidth={2}
                  x1={xScale(props.inServiceDate.toJSDate())}
                  x2={xScale(props.inServiceDate.toJSDate())}
                  y1={leftScale(preServiceValue)}
                  y2={leftScale(postServiceValue)}
                />
              )}

            {postServicePoints.length > 1 && (
              <polyline
                fill="none"
                points={postServicePoints
                  .map(
                    (point) =>
                      `${xScale(point.date.toJSDate())},${leftScale(point.value)}`
                  )
                  .join(' ')}
                stroke={theme.palette.secondary.main}
                strokeWidth={2}
              />
            )}

            {xAxisMonths.map((month) => (
              <text
                key={`tick-${month.toISODate()}`}
                fill={theme.palette.text.secondary}
                fontSize={11}
                textAnchor="middle"
                x={xScale(month.toJSDate())}
                y={innerHeight + 22}
              >
                {month.toFormat(
                  xAxisMonths.length > 24 ? 'MMM yy' : 'MMM yyyy'
                )}
              </text>
            ))}
          </g>
        </svg>
      </Box>

      {tooltipOpen && tooltipData !== undefined && (
        <TooltipInPortal
          left={tooltipLeft}
          style={{
            ...defaultStyles,
            backgroundColor: theme.palette.grey[900],
            borderRadius: 8,
            color: theme.palette.common.white,
            maxWidth: 340,
            padding: '12px',
            pointerEvents: 'none',
          }}
          top={(tooltipTop ?? 0) + SVG_MARGIN.top - TOOLTIP_VERTICAL_OFFSET_PX}
        >
          <DepreciationChartTooltip
            acquisitionCost={props.acquisitionCost}
            capitalizedCost={props.capitalizedCost}
            endOfUsefulLife={props.endOfUsefulLife}
            exitDate={props.disposalDate}
            inServiceDate={props.inServiceDate}
            currencyCode={props.currencyCode}
            salvageValue={props.salvageValue}
            themeBorderColor={theme.palette.grey[700]}
            tooltipData={tooltipData}
            usefulLifeMonths={props.usefulLifeMonths}
          />
        </TooltipInPortal>
      )}
    </Box>
  );
}

// Compare by value: Zod re-parses each refetch into fresh DateTimes, so references are never stable
export const DepreciationChart = memo(
  DepreciationChartComponent,
  areDepreciationChartPropsEqual
);

function areDepreciationChartPropsEqual(
  prev: DepreciationChartProps,
  next: DepreciationChartProps
): boolean {
  return (
    prev.acquisitionCost === next.acquisitionCost &&
    prev.capitalizedCost === next.capitalizedCost &&
    prev.salvageValue === next.salvageValue &&
    prev.usefulLifeMonths === next.usefulLifeMonths &&
    prev.svgRef === next.svgRef &&
    haveSameInstant(prev.inventoryDate, next.inventoryDate) &&
    haveSameInstant(prev.currentDate, next.currentDate) &&
    haveSameInstant(prev.chartEndDate, next.chartEndDate) &&
    haveSameInstant(prev.disposalDate, next.disposalDate) &&
    haveSameInstant(prev.endOfUsefulLife, next.endOfUsefulLife) &&
    haveSameInstant(prev.inServiceDate, next.inServiceDate) &&
    haveSamePeriods(prev.periods, next.periods)
  );
}

function handleTooltipMove(props: {
  barWidth: number;
  barX: number;
  event: MouseEvent<SVGRectElement>;
  period: NormalizedPeriod;
  periodEnd: DateTime;
  showTooltip: ReturnType<typeof useTooltip<HoverDatum>>['showTooltip'];
}) {
  const rect = props.event.currentTarget.getBoundingClientRect();

  props.showTooltip({
    tooltipData: {
      ...props.period,
      periodEnd: props.periodEnd,
      top: props.event.clientY - rect.top,
    },
    tooltipLeft: props.barX + props.barWidth / 2,
    tooltipTop: props.event.clientY - rect.top,
  });
}
